import { Box, Grid } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { MDBox } from '../../../../Component/Common/Components/MDBox';
import { MDTypography } from '../../../../Component/Common/Components/MDTypography';
import { useRecoilValue } from 'recoil';
import { VerificationCompanyAtom } from '../../../../State/VerificationCompany.Atom';
import { VerificationAgencyClientApplicationService } from '../../../../Component/Api/VerificationAgency/VerificationAgencyClientApplication/VerificationAgencyClientApplicationService';
import { IntermediateGlobalLoading } from '../../../../Component/Common/Components/Loading/IntermediateGlobalLoading';
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { RiskAssessmentValidationSchema } from './RiskAssessmentValidationSchema';
import { toast } from 'react-toastify';
import AppAccordion from '../../../../Component/Common/Components/Accordion/AppAccordion';
import AppCheckbox from '../../../../Component/Common/Components/FormValidationInputs/AppCheckbox';
import { LoadingButton } from '@mui/lab';
import { AppQuill } from '../../../../Component/Common/Components/FormValidationInputs/AppQuill';
import AppMultiSelect from '../../../../Component/Common/Components/FormValidationInputs/AppMultiSelect';
import { IDropDownOptions } from '../../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import { RiskAssessmentRelatedPartiesDataTable } from './RelatedPartiesDataTable';
import { HttpStatusCode } from '../../../../Component/Model/Enum/HttpStatusCode';
import { RiskAssessmentDto } from '../../../../Component/Model/Dto/VerificationAgency/Client/Application/RiskAssessment/RiskAssessment';
import { VerificationAgencyClientApplication } from '../../../../Component/Model/Dto/VerificationAgency/VerificationAgencyClientApplication';
import { getIOptionsArray } from '../../../../Helpers/arrayHelper';
import { PortalDocumentsDropZone } from '../../../Common/PortalDocuments/PortalDocumentsDropZone';
import { BEEScorecardUserAccessService } from '../../../../Component/Api/BBEScorecardUserAccess/BEEScorecardUserAccessService';
import { BEE123UserTypes } from '../../../../Component/Common/Constants/Lists/Users';

export const RiskAssessment = () => {
	const verificationCompany = useRecoilValue(VerificationCompanyAtom);
	const [riskAssessmentId, setRiskAssessmentId] = React.useState(-1);
	const [loading, setLoading] = React.useState(false);
	const [application, setApplication] = React.useState<VerificationAgencyClientApplication.VerificationAgencyClientApplicationResponse>();

	const [analysts, setAnalysts] = React.useState<IDropDownOptions[]>([]);
	const [technicalSignatory, setTechnicalSignatory] = React.useState<IDropDownOptions[]>([]);
	const [noAnalystUsersFound, setNoAnalystUsersFound] = useState(false);
	const [noSignatoryUsersFound, setNoSignatoryUsersFound] = useState(false);

	const link = 'RiskAssessment';

	const { control, handleSubmit, formState: { isSubmitting }, reset, watch, getValues } = useForm({
		mode: 'all',
		resolver: yupResolver(RiskAssessmentValidationSchema),
	});

	const areThereRelatedParties = watch("areThereRelatedParties");

	const handleDataSubmit = async (data: FieldValues) => {
		try {
			const formData = { ...data } as RiskAssessmentDto;
			formData.id = riskAssessmentId;
			formData.vaClientApplicationId = verificationCompany.applicationId;

			if (riskAssessmentId === -1) {
				const response = await VerificationAgencyClientApplicationService.CreateRiskAssessment(formData);
				resetFormData(response.data);
			} else {
				const response = await VerificationAgencyClientApplicationService.UpdateRiskAssessment(formData);
				resetFormData(response.data);
				toast.success('Risk Assessment saved');
			}
		} catch { }
	};

	const resetFormData = (data: RiskAssessmentDto) => {
		setRiskAssessmentId(data.id ?? -1);
		reset(data);
	};

	const fetchApplication = async () => {
		try {
			const response = await VerificationAgencyClientApplicationService.GetById(verificationCompany.applicationId);
			setApplication(response.data);
		} catch (error) {
			console.log(error);
		}
	};

	const fetchRiskAssessmentData = async () => {
		try {
			const applicationId = verificationCompany.applicationId;
			let response = await VerificationAgencyClientApplicationService.GetRiskAssessmentByApplicationId(applicationId);

			if (response.status === HttpStatusCode.NO_CONTENT) {
				const data = getValues();
				handleDataSubmit(data);
				return;
			}
			resetFormData(response.data);
		} catch {
		}
	}

	const fetchStaticData = async (selectedValue?: string) => {
		try {
			const analystUsers = await BEEScorecardUserAccessService.getAllScorecardUserAccessByType(BEE123UserTypes.User, selectedValue);
			const technicalUsers = await BEEScorecardUserAccessService.getAllScorecardUserAccessByType(BEE123UserTypes.SubAdmin, selectedValue);

			const analystUsersOptions = getIOptionsArray(analystUsers.data, "id", "name");
			const technicalUsersOptions = getIOptionsArray(technicalUsers.data, "id", "name");

			setAnalysts(analystUsersOptions.length > 0 ? analystUsersOptions : []);
			setNoAnalystUsersFound(analystUsersOptions.length === 0);

			setTechnicalSignatory(technicalUsersOptions.length > 0 ? technicalUsersOptions : []);
			setNoSignatoryUsersFound(technicalUsersOptions.length === 0);
		} catch {
		}
	};


	const fetchData = async () => {
		setLoading(true);
		if (verificationCompany.applicationId !== -1) {
			const applicationPromise = fetchApplication();
			const riskAssessmentPromise = fetchRiskAssessmentData();
			const staticDataPromise = fetchStaticData();
			await Promise.all([applicationPromise, riskAssessmentPromise, staticDataPromise]);
		}
		setLoading(false);
	};

	useEffect(() => {
		fetchData();
	}, [verificationCompany.applicationId]);

	if (loading) {
		return <IntermediateGlobalLoading />;
	}
	return (
		<Box>
			<MDBox style={{ marginBottom: '10px' }}>
				<MDTypography variant='h4' fontWeight='bold'>
					Application - {application?.referenceNumber}
				</MDTypography>
			</MDBox>
			<MDTypography graphy color='primary' variant='h6' fontWeight='bold' sx={{ mb: 1 }}>
				Risk Assessment
			</MDTypography>

			<Grid component='form' id='RiskAssessmentForm' onSubmit={handleSubmit(handleDataSubmit)}>
				<AppAccordion label='Competent Resources Available' expandOnDefault={true}>
					<AppCheckbox control={control} label='Sufficient capacity has been confirmed' name='confirmedSufficientCapacity' />
					<Grid container spacing={1} mt={0.1}>
						<Grid item xs={6}>
							<AppMultiSelect control={control} name="allocatedAnalysts" label="Allocated Analyst(s)" options={analysts} size="small" disabled={noAnalystUsersFound} />
						</Grid>
						<Grid item xs={6}>
							<AppMultiSelect control={control} name="technicalSignatories" label="Allocated Technical Signatory(s)" options={technicalSignatory} size="small" disabled={noSignatoryUsersFound} />
						</Grid>
					</Grid>
				</AppAccordion>

				<AppAccordion label='Related Parties' expandOnDefault={true}>
					<AppCheckbox control={control} label='The Technical Signatory is impartial' name='isTheTechnicalSignatoryImpartial' />
					<AppCheckbox control={control} label='The allocated Analyst(s) are impartial' name='isTheAllocatedAnalystsImpartial' />
					<AppCheckbox control={control} label='There are related parties' name='areThereRelatedParties' />

					{areThereRelatedParties &&
						<MDBox style={{ marginTop: '10px' }}>
							<RiskAssessmentRelatedPartiesDataTable riskAssessmentId={riskAssessmentId} />
						</MDBox>
					}

				</AppAccordion>

				<AppAccordion label='General' expandOnDefault={true}>
					<AppCheckbox control={control} label='Sufficient information was supplied' name='sufficientInformationWasSupplied' />
					<AppCheckbox control={control} label='Verification requirements were provided' name='verificationRequirementsProvided' />
					<AppCheckbox control={control} label='There are no differences in understanding' name='confirmedNoDifferencesInUnderstanding' />
					<AppQuill control={control} name="additionalText" placeholder="Add text..." />

					{riskAssessmentId > 0 &&
						<Box mt={1}>
							<PortalDocumentsDropZone link={link} id={riskAssessmentId} />
						</Box>
					}
				</AppAccordion>

			</Grid>
			<LoadingButton loading={isSubmitting} variant='contained' form='RiskAssessmentForm' type='submit' color='primary' sx={{ mt: 1 }}>
				Save
			</LoadingButton>
		</Box>
	);
};
